import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AgentServicev2, Agent } from '../agent-builder-services/agent.service';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import modelsData from '../agent-builder-config/models.json';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'agent-builder-list',
  templateUrl: './agent-builder-list.component.html',
  styleUrls: ['./agent-builder-list.component.scss'],
  standalone: false
})
export class AgentBuilderListComponent implements OnInit {

  agents: Agent[] = [];
  loading = false;
  searchText = '';
  selectedAgentId = signal<Agent | null>(null);
  editing = false;
  sub1:Subscription;
  form: FormGroup;
  pageIndex =1;
  editSubject:BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private agentService: AgentServicev2,
    private message: NzMessageService,
    private modal: NzModalService,
    private fb: FormBuilder,
    private datePipe: DatePipe
  ) {}

  onBackFromConfig(){
    this.selectedAgentId = undefined;
    this.loadAgents();
  }

  ngOnDestroy(): void { 
    if ( this.sub1 != null){
        this.sub1.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.loadAgents();
    
    this.sub1 = this.editSubject.subscribe(editing =>{
        this.editing = editing;
        if (!editing){
            this.onBackFromConfig()
        }
    });   
  }

  /**
   * Carrega lista de agentes
   */
  private loadAgents(): void {
    setTimeout(() => {
      this.loading = true;
    }, 0);
    this.agentService.agents$.subscribe({
      next: (agents) => {
        this.agents = agents;
        this.loading = false;
      },
      error: () => {
        this.loading = false;   
      }
    });
    this.agentService.loadAgents();
  }

  /**
   * Filtra agentes por nome
   */
  get filteredAgents(): Agent[] {
    const search = this.searchText?.trim().toLowerCase();

    if (!search) {
        return this.agents; // sem texto → retorna tudo
    }
    const start = (this.pageIndex - 1) * 10;

    let filtered =  this.agents.filter(agent => {
        return Object.values(agent)
        .filter(v => v !== null && v !== undefined)
        .some(v => v.toString().toLowerCase().includes(search));
    });
    return filtered.slice(start, start + 10);
}

  /**
   * Abre formulário para criar novo agente
   */
  createAgent(): void {
    this.form.reset(this.valoresPadraoDoAgente());
    this.editSubject.next(true);
  }

  /**
   * Valores iniciais de um agente. Usado tanto na criacao quanto antes de
   * carregar um agente existente, para que campo ausente volte ao padrao em
   * vez de herdar o valor do agente editado anteriormente.
   */
  private valoresPadraoDoAgente(): object {
    return {
      temperature: 0.3,
      maxTokens: 2048,
      topP: 1,
      stream: false,
      autonomo: false,
      maxIterator: 5,
      mcps: [],
      goal: [],
      allowedAgents: [],
      systemPromptHistory: [],
      usePromptBuilder: false,
      ragTopK: 8,
      ragMinScore: 0,
      ragTopKUserDoc: 30,
      maxToolIterations: 10,
      maxHistoryMessages: 40,
      resumirHistorico: false,
      pipeline: ['glossario', 'kb', 'feedback', 'userdoc'],
      dicTable: 'dic',
      active: true,
      servername: null,
      baseUrl: ''
    };
  }

  /**
   * Abre formulário para editar agente
   */
  editAgent(agent: Agent): void {
    // limpa antes de carregar: sem isso, um campo que o agente selecionado nao
    // possui mantem o valor do agente aberto anteriormente
    this.form.reset(this.valoresPadraoDoAgente());
    this.form.patchValue(agent);
    this.editSubject.next(true);
  }

  /**
   * Deleta agente
   */
  deleteAgent(agent: Agent): void {
    
    if (!agent._id) {
      this.message.error('Agente inválido');
      return;
    }

    this.modal.confirm({
      nzTitle: 'Confirmar exclusão',
      nzContent: `Tem certeza que deseja deletar o agente "${agent.agentName}"?`,
      nzOkText: 'Sim',
      nzCancelText: 'Não',
      nzOkDanger: true,
      nzOnOk: () => {
        this.loading = true;
        this.agentService.deleteAgent(agent._id).subscribe({
          next: () => {
            this.loading = false;
            this.message.success('Agente deletado com sucesso');
            this.agentService.refreshAgents();
          },
          error: () => {
            this.loading = false;
            this.message.error('Erro ao deletar agente');
          }
        });
      }
    });
  }

    getIcon(agent: Agent): string {
        
        const icon = modelsData.find(m => m.llm === agent.llm)?.icon;
        return icon || 'question-circle';
    }

  
    cloneAgent(agent: Agent): void {
        const clonedAgent = { ...agent };
        delete clonedAgent._id;
        clonedAgent.agentName = `${agent.agentName} (Cópia)`;

        this.loading = true;
        this.agentService.createAgent(clonedAgent).subscribe({
        next: () => {
            this.loading = false;
            this.message.success('Agente clonado com sucesso');
            this.agentService.refreshAgents();
        },
        error: () => {
            this.loading = false;
            this.message.error('Erro ao clonar agente');
        }
        });
    }

    refreshAgents(): void {
        
        this.loadAgents();
    }

  /**
   * Formata tamanho de arquivo
   */
  formatFileSize(bytes?: number): string {
    if (!bytes) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Formata data
   */
  formatDate(date?: Date): string {
    if (!date) return '-';
    return this.datePipe.transform(date, 'yyyy-MM-dd HH:mm:ss', 'America/Sao_Paulo') || '-';
  }


  private initForm(): void {
    this.form = this.fb.group({
      model: ['', Validators.required],
      codecontract: [null, Validators.required],
      codecompany: [null, Validators.required],
      llm: ['', Validators.required],
      agentName: ['', Validators.required],
      embeedModel: ['', Validators.required],
      systemPrompt: ['', Validators.required],
      apiKey: ['', Validators.required],
      fbTable: ['', Validators.required],
      kbTable: ['', Validators.required],
      qaTable: ['', Validators.required],
      dbName: ['', Validators.required],
      temperature: [0.3, Validators.required],
      maxTokens: [2048, Validators.required],
      topP:[1, Validators.required],
      devMessage: [''],
      kbVectorIndexName: ['', Validators.required],
      fbVectorIndexName: ['', Validators.required],
      welcomeMessage: [''],
      stream: [false, Validators.required],
      autonomo: [false, Validators.required],
      alTable: [''],
      maxIterator: [5],
      mcps: [[]],
      goal: [[]],
      allowedAgents: [[]],
      _id: [''],
      systemPromptHistory:[[]],
      cron: [null],

      // Construtor de prompt: quando ligado, o Java monta o prompt pelas secoes
      // abaixo (OrchestratorPromptBuilder) em vez de usar o systemPrompt cru.
      usePromptBuilder: [false],
      promptBuilder: this.fb.group({
        sectionFixedCore: [''],
        sectionIdentity: [''],
        sectionStrategy: [''],
        sectionRules: [''],
        sectionStyle: [''],
        sectionSafety: ['']
      }),

      // Ajuste fino da busca na base de conhecimento
      ragTopK: [8],
      ragMinScore: [0],
      ragTopKUserDoc: [30],

      // Limites de execucao: o Java tem os mesmos padroes, entao deixar em branco
      // nao quebra nada - so tira do usuario a chance de ajustar
      maxToolIterations: [10],
      maxHistoryMessages: [40],
      resumirHistorico: [false],

      // Ordem dos contextos: o mesmo padrao do Java. O que sai da lista deixa
      // de rodar, e cada etapa desligada poupa um embedding e uma busca.
      pipeline: [['glossario', 'kb', 'feedback', 'userdoc']],

      // Dicionario canonico: mesma ideia de kbTable/qaTable, uma colecao no
      // banco do agente
      dicTable: ['dic'],

      // Os dois campos que o loadAgents do Java exige para carregar um agente
      // autonomo, junto com autonomo:true. Sem eles o ciclo nunca ve o agente.
      active: [true],
      servername: [null],

      // Endereco base da LLM: o provedor llama monta as URLs a partir dele
      baseUrl: ['']
    });

   
  }
}
